import React, { useMemo } from 'react'
import { BoxProps } from 'rebass'

import { MeQuery, useMyUpcomingPaymentQuery } from '../../types/graphql'

import { useTranslation, Trans } from '../../i18n'
import SLoadingIndicator from 'components/opinionless/SLoadingIndicator'
import HistorySvg from '@svgs/history.svg'
import { formatMoney } from '../../utils/money'
import moment from 'moment-timezone'
import ConditionalRender from '../../utils/conditionalRender'
import { AddToCalendarButton } from 'add-to-calendar-button-react'
import styles from './style.module.scss'
import XCard, { XCARD_TYPE } from 'v2/containers/xCard'
import XIcon from 'v2/components/XIcon'
import XText from 'v2/components/XText'
import { XTEXT_SIZE } from 'v2/styles/constants'
import XLink from 'v2/components/XLink'
import XButton from 'v2/components/XButton'
import XColors from 'v2/styles/colors'
import { useAuthenticatedUser } from 'v2/context/user'
type Props = BoxProps

const HomeUpcomingPayment: React.FC<Props> = (props: Props) => {
    const { t } = useTranslation(window.LANG_NS)

    const { data, loading } = useMyUpcomingPaymentQuery()
    const { authenticatedUser, transferMethods } = useAuthenticatedUser()

    const upcomingPaymentAmountStr = useMemo(() => {
        if (data && data.myUpcomingPayment) {
            return formatMoney(data.myUpcomingPayment.amount)
        } else {
            return null
        }
    }, [data])

    const transferMethod = useMemo(() => {
        if (!authenticatedUser || !authenticatedUser.paymentProfile?.transferMethods.edges.length) {
            return null
        } else {
            const edge = authenticatedUser.paymentProfile.transferMethods.edges.find(
                (transferMethodEdge: MeQuery['me']['paymentProfile']['transferMethods']['edges'][0]) =>
                    transferMethodEdge.node.isCurrentMethod
            )
            if (edge) {
                return edge.node
            } else {
                return null
            }
        }
    }, [authenticatedUser, transferMethods])

    const shouldBeDisplayed = useMemo(() => {
        return transferMethod?.type === 'PREPAID_CARD' && transferMethod?.isCurrentMethod
    }, [transferMethod])

    const upcomingPaymentDateStr = useMemo(() => {
        if (data && data.myUpcomingPayment) {
            const scheduledDate = moment(data.myUpcomingPayment.scheduledDate).tz(window.ORGANIZATION_CONFIGS.timezone)
            if (scheduledDate.isSame(new Date(), 'day')) {
                return `${scheduledDate.format('MMM D, YYYY')}(${t('today')})`
            }
            if (scheduledDate.isSame(moment().add(1, 'day'), 'day')) {
                return `${scheduledDate.format('MMM D, YYYY')}(${t('tomorrow')})`
            }
            return scheduledDate.format('MMMM D, YYYY')
        } else {
            return null
        }
    }, [data, t])

    const getUpcomingPaymentTitleForCalendar = () => {
        if (window.PROGRAM?.name) {
            return window?.PROGRAM?.name + ' Payment'
        } else {
            return 'Payment'
        }
    }

    const getPaymentDate = () => {
        if (data && data.myUpcomingPayment) {
            const scheduledDate = moment(data.myUpcomingPayment.scheduledDate).tz(window.ORGANIZATION_CONFIGS.timezone)
            return scheduledDate.format('YYYY-MM-DD')
        } else {
            return null
        }
    }

    return (
        <div className={styles.payment__wrapper_upcoming}>
            <XCard type={XCARD_TYPE.HOME}>
                <div className={styles.payment__title}>
                    <XIcon icon={HistorySvg} size={20} tintColor="black" />
                    <div className={styles.payment__transfer_last}>
                        <XText color={XColors.black} size={XTEXT_SIZE.LARGE_SEMI_BOLDED}>
                            {t('your_next_payment')}
                        </XText>
                    </div>
                </div>
                <div className={styles.payment__date}>
                    {loading ? (
                        <SLoadingIndicator />
                    ) : (
                        <React.Fragment>
                            <ConditionalRender if={upcomingPaymentAmountStr}>
                                <div
                                    className={
                                        shouldBeDisplayed
                                            ? styles.payment__date__text
                                            : styles.payment__date__text__smaller
                                    }
                                >
                                    <XText size={XTEXT_SIZE.LARGE_UNBOLDED} color={'black'}>
                                        {upcomingPaymentAmountStr}
                                    </XText>{' '}
                                    <XText size={XTEXT_SIZE.LARGE_UNBOLDED} color={'black'}>
                                        on
                                    </XText>
                                    <XText size={XTEXT_SIZE.LARGE_UNBOLDED} color={'black'}>
                                        {upcomingPaymentDateStr}
                                    </XText>
                                </div>
                            </ConditionalRender>
                            <ConditionalRender if={!upcomingPaymentAmountStr}>
                                <XText color={XColors.black} size={XTEXT_SIZE.LARGE_UNBOLDED}>
                                    {t('no_upcoming_payments')}
                                </XText>
                            </ConditionalRender>
                        </React.Fragment>
                    )}
                </div>
                <ConditionalRender if={upcomingPaymentAmountStr}>
                    <div className={styles.payment__button}>
                        <AddToCalendarButton
                            name={getUpcomingPaymentTitleForCalendar()}
                            options={['Apple', 'Google']}
                            startDate={getPaymentDate()}
                            endDate={getPaymentDate()}
                            startTime="00:00"
                            endTime="23:59"
                            timeZone={Intl.DateTimeFormat().resolvedOptions().timeZone}
                            hideIconButton={false}
                            location={`${window.ORGANIZATION.webAppUrl}/v2${window?.PROGRAM?.urlFragment}/login`}
                            hideCheckmark={true}
                            hideBackground={true}
                            label={t('add_to_calendar')}
                            buttonStyle="custom"
                            description={`Your payment will be sent out the morning of ${getPaymentDate()}. Direct Deposit payments can take up to  24-48 hours for your bank to process the payment. If you have any questions please reach out to the F4GI Customer Service team through the payment portal ${
                                window.ORGANIZATION.webAppUrl
                            }/v2${window?.PROGRAM?.urlFragment}/login, or Call +1 833 801 0464`}
                            customCss={
                                ['local', 'development'].includes(process.env.NEXT_PUBLIC_ENVIRONMENT_NAME)
                                    ? 'http://localhost:3000/add-to-calendar.css'
                                    : `${window.ORGANIZATION.webAppUrl}/add-to-calendar.css`
                            }
                        ></AddToCalendarButton>
                    </div>
                </ConditionalRender>
            </XCard>
        </div>
    )
}

export default HomeUpcomingPayment
